// Datasets.jsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Flex,
  Link,
  Text,
  Heading,
  VStack,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Container,
  Tag,
  HStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { DATASET_LIST } from "./DatasetsData";

function DatasetsHeroHeader() {
  const accent = "#16A34A";
  const accent2 = "#22C55E";

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");

  const heroBg = useColorModeValue(
    "radial-gradient(1000px 420px at 12% 0%, rgba(34,197,94,0.18), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(22,163,74,0.14), transparent 60%), linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.00))",
    "radial-gradient(1000px 420px at 12% 0%, rgba(34,197,94,0.20), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(22,163,74,0.14), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
  );

  return (
    <Box pt={{ base: 6, md: 8 }} pb={{ base: 8, md: 10 }} bg={heroBg} borderBottom="1px solid" borderColor={border}>
      <Container maxW="1000px">
        <HStack spacing={2} flexWrap="wrap">
          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
            border="1px solid"
            borderColor={border}
            fontWeight="800"
          >
            University of Ottawa
          </Tag>

          <Tag
            size="lg"
            borderRadius="full"
            bg={useColorModeValue("rgba(34,197,94,0.14)", "rgba(34,197,94,0.18)")}
            color={useColorModeValue("#166534", "#86EFAC")}
            fontWeight="900"
          >
            NEXTCON Lab • SCVIC
          </Tag>
        </HStack>

        <Heading mt={4} fontSize={{ base: "3xl", md: "4xl" }} color={titleColor} letterSpacing="tight">
          <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
            Datasets
          </Box>
        </Heading>

        <Text mt={3} color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="85ch" lineHeight={1.8}>
          Public research datasets developed within NEXTCON Lab & SCVIC,
          supporting intelligent transportation systems, V2X communications,
          edge intelligence, and AI-driven networking research.
        </Text>
      </Container>
    </Box>
  );
}

function DatasetCard({ ds, maxLength = 250 }) {
  const [expanded, setExpanded] = useState(false);

  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const titleColor = useColorModeValue("#0F172A", "white");

  const isLong = (ds.description || "").length > maxLength;
  const textToShow = expanded ? ds.description : (ds.description || "").slice(0, maxLength);

  return (
    <Box
      p={6}
      border="1px solid"
      borderColor={border}
      borderRadius="18px"
      bg={cardBg}
      boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.06)", "none")}
      _hover={{ transform: "translateY(-4px)", transition: "0.25s ease" }}
      minW={{ base: "100%", md: "380px" }}
    >
      <Flex direction={{ base: "column", md: "row" }} gap={4} mb={4}>
        {ds.imgIcon && (
          <Box
            as="img"
            src={ds.imgIcon}
            alt={`${ds.name} thumbnail`}
            boxSize={{ base: "100%", md: "160px" }}
            objectFit="cover"
            borderRadius="md"
            shadow="md"
          />
        )}

        <Box flex="1">
          <Text fontSize="lg" fontWeight="800" color={titleColor} mb={2}>
            {ds.name}
          </Text>

          {Array.isArray(ds.Authors) && ds.Authors.length > 0 && (
            <>
              <Text fontSize="sm" color="gray.500">
                <b>Citation Author(s):</b>
              </Text>
              {ds.Authors.map((line, i) => (
                <Text key={i} fontSize="sm" color="gray.500">
                  {line}
                </Text>
              ))}
            </>
          )}
        </Box>
      </Flex>

      <Text fontSize="sm" fontWeight="600" color="gray.500">
        Description:
      </Text>

      <Text fontSize="sm" color="gray.600">
        {textToShow}
        {!expanded && isLong && "..."}
      </Text>

      {isLong && (
        <Text
          fontSize="sm"
          color="green.600"
          mt={1}
          cursor="pointer"
          fontWeight="600"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Read less" : "Read more"}
        </Text>
      )}

      <Text fontSize="sm" mt={3} color="gray.600">
        <b>Format:</b> {ds.format}
      </Text>

      <Text fontSize="sm" color="gray.600">
        <b>Published:</b> {ds.date}
      </Text>

      <Flex mt={3}>
        <Link href={ds.link} color="green.600" fontWeight="700" isExternal>
          IEEE Dataport Link →
        </Link>
      </Flex>
    </Box>
  );
}

function Datasets() {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);

  const ITEMS_PER_PAGE = 6;

  const pageBg = useColorModeValue("gray.50", "rgba(255,255,255,0.02)");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");

  // Filter datasets
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return DATASET_LIST;

    return DATASET_LIST.filter(
      (d) =>
        (d.name || "").toLowerCase().includes(q) ||
        (d.description || "").toLowerCase().includes(q)
    );
  }, [query]);

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [query]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const start = page * ITEMS_PER_PAGE;
  const currentItems = filtered.slice(start, start + ITEMS_PER_PAGE);

  return (
    <Box bg={pageBg} minH="100vh">
      <DatasetsHeroHeader />

      {/* ✅ SEARCH BAR AT TOP */}
      <Container maxW="1000px" pt={8} pb={4}>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon opacity={0.65} />
          </InputLeftElement>

          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search datasets (title or description)..."
            borderRadius="18px"
            borderColor={border}
            bg={useColorModeValue("white", "rgba(255,255,255,0.04)")}
            _focus={{
              borderColor: "rgba(34,197,94,0.65)",
              boxShadow: "0 0 0 1px rgba(34,197,94,0.65)",
            }}
          />

          {query.trim() ? (
            <IconButton
              aria-label="Clear"
              icon={<CloseIcon boxSize={3} />}
              ml={2}
              borderRadius="18px"
              variant="outline"
              borderColor={border}
              onClick={() => setQuery("")}
            />
          ) : null}
        </InputGroup>

      </Container>

      {/* LIST */}
      <Container maxW="1000px" pb={10}>
        <VStack spacing={6} align="stretch">
          {currentItems.map((ds, index) => (
            <DatasetCard key={index} ds={ds} />
          ))}
        </VStack>

        {/* PAGINATION */}
        {filtered.length > ITEMS_PER_PAGE && (
          <Flex justify="center" align="center" gap={4} mt={10}>
            <IconButton
              icon={<ChevronLeftIcon />}
              aria-label="Previous"
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              isDisabled={page === 0}
              borderRadius="full"
            />

            <Text fontWeight="600" color={subColor}>
              Page {page + 1} of {totalPages}
            </Text>

            <IconButton
              icon={<ChevronRightIcon />}
              aria-label="Next"
              onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
              isDisabled={page === totalPages - 1}
              borderRadius="full"
            />
          </Flex>
        )}
      </Container>
    </Box>
  );
}

export default Datasets;